import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import {
  Star, Calculator, MoonStars, Tree, PawPrint, Bird, Compass,
  Heart, Briefcase, Pulse, Sparkle, ArrowLeft, Crown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import { useSavedReportLoader } from "../lib/useSavedReport";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";

export default function NakshatraAnalysisPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", timezone: "Asia/Kolkata", place: "" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSavedReportLoader({ expectedTool: "nakshatra", setForm, setResult });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time) {
      toast.error("Please enter your date and time of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/nakshatra/analyze", form);
      setResult(data);
      setTimeout(() => {
        const el = document.querySelector("[data-testid='nak-result']");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your nakshatra. Please check the inputs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="nakshatra-page">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="nak-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Star size={14} weight="duotone" /> Free · Vedic Janma Nakshatra
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Nakshatra <span className="italic text-gold">Deep Analysis</span>
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          The 27 nakshatras are the lunar mansions — your <b className="text-[#3F2D1B]">Janma Nakshatra</b> (the one the Moon was in at your birth) reveals your soul's blueprint far more precisely than your sun sign. Enter your birth details and we'll surface your ruling planet, deity, symbol, yoni, gana, nadi, bird, animal, tree, sacred syllables and a deep strengths-weaknesses-career-love-health reading.
        </p>

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="nak-date" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input type="time" required value={form.time} onChange={handle("time")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="nak-time" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Timezone</span>
              <select value={form.timezone} onChange={handle("timezone")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="nak-tz">
                <option value="Asia/Kolkata">India (IST · UTC+5:30)</option>
                <option value="Asia/Dubai">UAE (UTC+4)</option>
                <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                <option value="Europe/London">London (UTC+0/+1)</option>
                <option value="America/New_York">New York (UTC-5/-4)</option>
                <option value="America/Los_Angeles">Los Angeles (UTC-8/-7)</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Place of birth (optional)</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) =>
                    setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
                  }
                  placeholder="Mumbai, India"
                  testId="nak-place"
                  required={false}
                />
              </div>
            </label>
          </div>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="nak-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Reading the stars..." : "Reveal my nakshatra"}
          </button>
        </form>

        {/* Result */}
        {result && (
          <div className="mt-10 space-y-5" data-testid="nak-result">
            {/* Hero card */}
            <div className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40">
              <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
                <MoonStars size={14} weight="fill" /> Janma Nakshatra · #{result.index} of 27
              </div>
              <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">{result.name}</h2>
              <div className="mt-1 text-amber-200/80 text-sm">
                {result.signs_covered} · Pada {result.pada} of 4 · Moon at {result.moon_longitude}°
              </div>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Pill label="Ruling planet" value={result.ruling_planet} accent />
                <Pill label="Deity" value={result.deity} />
                <Pill label="Symbol" value={result.symbol} />
                <Pill label="Element" value={result.element} />
              </div>
            </div>

            {/* Classical 8-attribute compass */}
            <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="nak-compass">
              <div className="label-eyebrow text-gold flex items-center gap-1.5">
                <Compass size={14} weight="duotone" /> Classical attributes
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <Attr icon={Crown} label="Varna" value={result.varna} hint="Spiritual caste vibration" />
                <Attr icon={Sparkle} label="Gana" value={result.gana} hint="Temperament — divine, human, or demonic energy" />
                <Attr icon={PawPrint} label="Yoni" value={`${result.yoni} (${result.yoni_gender})`} hint="Sexual/instinctive nature; key in matchmaking" />
                <Attr icon={Pulse} label="Nadi" value={result.nadi} hint="Dosha emphasis — Aadi/Vata, Madhya/Pitta, Antya/Kapha" />
                <Attr icon={Bird} label="Bird" value={result.bird} hint="Totem bird — call to it in meditation" />
                <Attr icon={PawPrint} label="Animal" value={result.animal} hint="Power animal — your spirit kin" />
                <Attr icon={Tree} label="Tree" value={result.tree} hint="Sacred tree — worship/sit under it on your nakshatra day" />
                <Attr icon={Compass} label="Direction" value={result.direction} hint="Auspicious facing direction" />
                <Attr icon={MoonStars} label="Body part" value={result.body_part} hint="Sensitive body parts — care for them" />
              </div>
              <div className="mt-5 pt-4 border-t border-soft grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="label-eyebrow !text-[10px]">Sacred syllables</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {result.syllables.map((s) => (
                      <span key={s} className="px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-[#3F2D1B] font-serif-display text-base">{s}</span>
                    ))}
                  </div>
                  <div className="text-muted text-[11px] mt-1">Names starting with these syllables are auspicious for you.</div>
                </div>
                <div>
                  <div className="label-eyebrow !text-[10px]">Pada {result.pada} lord</div>
                  <div className="mt-1 text-[#3F2D1B] text-lg font-serif-display">{result.pada_lord} <span className="text-muted text-sm">({result.pada_navamsa} navamsa)</span></div>
                  <div className="text-muted text-[11px] mt-1">Adds a flavour on top of the main nakshatra ruler ({result.ruling_planet}).</div>
                </div>
              </div>
            </div>

            {/* Strengths / Weaknesses + Life-area reads — PREMIUM */}
            <PaywallGate tool="nakshatra" toolLabel="Nakshatra Deep Analysis" birthDetails={form} report={result}>
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="card-surface rounded-2xl p-5 sm:p-6 bg-emerald-50 border border-emerald-200" data-testid="nak-strengths">
                    <div className="label-eyebrow text-emerald-700">Strengths</div>
                    <ul className="mt-3 space-y-2 text-sm text-emerald-950">
                      {result.strengths?.map((s, i) => (
                        <li key={i} className="flex items-start gap-2"><span className="text-emerald-600 mt-0.5">✦</span><span>{s}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="card-surface rounded-2xl p-5 sm:p-6 bg-rose-50 border border-rose-200" data-testid="nak-weaknesses">
                    <div className="label-eyebrow text-rose-700">Watch-outs</div>
                    <ul className="mt-3 space-y-2 text-sm text-rose-950">
                      {result.weaknesses?.map((s, i) => (
                        <li key={i} className="flex items-start gap-2"><span className="text-rose-500 mt-0.5">⚠</span><span>{s}</span></li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <LifeCard icon={Briefcase} label="Career & calling" text={result.career} tint="amber" />
                  <LifeCard icon={Heart} label="Love & relationships" text={result.love} tint="rose" />
                  <LifeCard icon={Pulse} label="Health & body" text={result.health} tint="sky" />
                  <LifeCard icon={Sparkle} label="Spiritual path" text={result.spiritual} tint="indigo" />
                </div>
              </div>
            </PaywallGate>

            {/* CTA — Nakshatra specialist */}
            <ToolAstrologerCta
              requiredTechnique="Nakshatra-based Predictions"
              pitch={`Want a live read on your ${result.name} nakshatra dasha, padas & timing?`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Pill({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-amber-300/80">{label}</div>
      <div className={`mt-0.5 font-serif-display text-base sm:text-lg leading-tight ${accent ? "text-amber-300" : "text-amber-50"}`}>{value || "—"}</div>
    </div>
  );
}

function Attr({ icon: Icon, label, value, hint }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 label-eyebrow !text-[10px]">
        <Icon size={13} weight="duotone" className="text-gold" /> {label}
      </div>
      <div className="mt-1 text-sm font-medium text-[#3F2D1B]">{value || "—"}</div>
      {hint && <div className="text-muted text-[11px] mt-0.5 leading-snug">{hint}</div>}
    </div>
  );
}

function LifeCard({ icon: Icon, label, text, tint }) {
  const tones = {
    amber: "bg-amber-50 border-amber-200 text-amber-950",
    rose: "bg-rose-50 border-rose-200 text-rose-950",
    sky: "bg-sky-50 border-sky-200 text-sky-950",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-950",
  };
  return (
    <div className={`card-surface rounded-2xl p-5 sm:p-6 ${tones[tint]} border`}>
      <div className="flex items-center gap-2 label-eyebrow !text-[10px]">
        <Icon size={14} weight="duotone" /> {label}
      </div>
      <p className="text-sm mt-2 leading-snug">{text}</p>
    </div>
  );
}
